/**
 * @file 教案卷打印模板
 * @description 对应客户端 KnowledgePrintTemplate：核心知识点、巩固记忆、常考题型、牛刀小试、巩固练习与答案。
 *
 * @whenToUse 试卷 1（paper1）的打印预览。
 * @whenNotToUse 阶段卷与精简教案卷。
 */

import { QuestionType } from '@/enum/common.ts'
import type { PaperBlock } from '@/components/pages/handout-page/print/blocks.tsx'
import {
  buildPaperAnswerQrCodeDataUrl,
  type HandoutPaperModel,
  type PaperQuestion,
} from '@/components/pages/handout-page/print/paper-data.ts'
import { PaperPages } from '@/components/pages/handout-page/print/paper-pages.tsx'
import type { PaperPaginator } from '@/components/pages/handout-page/print/paginate.ts'
import { usePaperLayout } from '@/components/pages/handout-page/print/use-paper-layout.ts'

/**
 * 主观题需要答题留白。
 * @param question 题目
 */
function needsPlaceholder(question: PaperQuestion): boolean {
  return question.type === QuestionType.APPLICATION || question.type === QuestionType.SHORT_ANSWER
}

/**
 * 单题块。
 * @param question 题目
 * @param index 从 0 起的下标
 * @param type 块类型
 * @param withPlaceholder 是否留白
 */
function toQuestionBlock(
  question: PaperQuestion,
  index: number,
  type: PaperBlock['type'],
  withPlaceholder: boolean,
): PaperBlock {
  return {
    type,
    isLast: index === 0,
    data: {
      index: index + 1,
      question: question.questionHtml,
      needPlaceholder: withPlaceholder && needsPlaceholder(question),
      options: question.type === QuestionType.SELECT ? question.options ?? null : null,
    },
  }
}

/**
 * 写入一组答案。
 * @param pager 分页器
 * @param title 小标题
 * @param questions 题目
 */
async function addAnswers(
  pager: PaperPaginator,
  title: string,
  questions: PaperQuestion[],
) {
  await pager.add({ type: 'answer-subtitle', data: { subTitle: title } })
  for (let index = 0; index < questions.length; index += 1) {
    const question = questions[index]
    await pager.add({
      type: 'answer-data',
      isLast: index === questions.length - 1,
      data: {
        index: index + 1,
        answerHtml: question.answerHtml,
        analysisHtml: question.analysisHtml,
      },
    })
  }
}

/**
 * 按教案卷顺序分页。
 * @param pager 分页器
 * @param model 试卷
 */
async function fillKnowledge(pager: PaperPaginator, model: HandoutPaperModel) {
  if (model.coreKnowledgePoints.length > 0) {
    await pager.add({
      type: 'block_title',
      data: {
        icon: 'knowledge',
        title: '核心知识点学习',
        description: '请认真理解，不明白的及时圈出。',
      },
    })
    await pager.addColumns(
      model.coreKnowledgePoints.map((html, index) => ({ index: String(index + 1), html })),
      'coreKnowledgePoints',
    )
  }

  if (model.consolidationQuestions.length > 0) {
    await pager.add({
      type: 'block_title',
      data: {
        icon: 'gonggu',
        title: '巩固记忆学习',
        description: '请认真填写，不会的地方及时查看上方核心知识点。',
      },
    })
    await pager.addColumns(
      model.consolidationQuestions.map((html, index) => ({ index: String(index + 1), html })),
      'consolidationQuestions',
    )
  }

  if (model.learnWithMeList.length > 0) {
    await pager.add({
      type: 'block_title',
      data: { icon: 'genwoxue', title: '常考题型', description: '逐个击破！！！' },
    })
    for (let index = 0; index < model.learnWithMeList.length; index += 1) {
      const question = model.learnWithMeList[index]
      await pager.add({
        type: 'learnWithMeList',
        isLast: index === 0,
        items: [
          {
            index: index + 1,
            prefix: '典例',
            suffix: ':',
            html: question.questionHtml,
            options: question.type === QuestionType.SELECT ? question.options ?? null : null,
          },
          {
            index: '',
            prefix: '讲解',
            suffix: ':',
            html: question.analysisHtml,
          },
        ],
      })
    }
    await pager.add({
      type: 'block_footer',
      text: '（注重解析，在不会做题的时候，解析会提供做题方法，解题思路！！！）',
    })
  }

  if (model.tryPracticeQuestions.length > 0) {
    await pager.add({
      type: 'block_title',
      data: { icon: 'xiaoshiniudao', title: '牛刀小试', description: '我来试一试！！！' },
    })
    for (let index = 0; index < model.tryPracticeQuestions.length; index += 1) {
      await pager.add(toQuestionBlock(model.tryPracticeQuestions[index], index, 'tryPracticeQuestions', false))
    }
  }

  if (model.adaptivePracticeQuestions.length > 0) {
    await pager.add({
      type: 'block_title',
      data: { icon: 'lianxi', title: '巩固练习', description: '我要开始练习啦！' },
    })
    for (let index = 0; index < model.adaptivePracticeQuestions.length; index += 1) {
      await pager.add(
        toQuestionBlock(model.adaptivePracticeQuestions[index], index, 'adaptivePracticeQuestions', true),
      )
    }
  }

  if (!model.printAnswers) return

  await pager.breakPage()
  await pager.add({ type: 'answer-prefix' })
  await pager.add({
    type: 'block_title',
    data: { title: '答案', description: '按模块查看本卷答案与讲解。' },
  })
  await addAnswers(pager, '牛刀小试答案', model.tryPracticeQuestions)
  await addAnswers(pager, '巩固练习答案', model.adaptivePracticeQuestions)

  if (model.header.paperNo) {
    const qrCodeDataUrl = await buildPaperAnswerQrCodeDataUrl(model.header.paperNo)
    await pager.add({
      type: 'paper-qr-code',
      data: { paperNo: model.header.paperNo, qrCodeDataUrl },
    })
  }
}

/**
 * 教案卷（试卷 1）。
 */
export function KnowledgePrintTemplate({
  model,
  onReady,
}: {
  /** 试卷数据 */
  model: HandoutPaperModel
  /** 分页结束 */
  onReady?: (ready: boolean) => void
}) {
  const layout = usePaperLayout('knowledge', model, fillKnowledge, onReady)
  return (
    <PaperPages
      pages={layout.pages}
      ready={layout.ready}
      rootRef={layout.rootRef}
      containers={layout.containers}
      header={model.header}
    />
  )
}
